import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { CreateTransferOrderDTO, FavoriteItemGroupDTO, LocationDTO } from '../../../api/api';
import CriticalOrderPopover from '../../../blocks/critical-order-popover/CriticalOrderPopOver';
import Button from '../../../components/button/Button';
import DatePicker from '../../../components/date-picker/DatePicker';
import LoadingOverlay from '../../../components/loading-overlay/LoadingOverlay';
import BasicPopover from '../../../components/popover/Popover';
import { Tab, Tabs } from '../../../components/tabs-horizontal/TabsHorizontal';
import TextField from '../../../components/text-field/TextField';
import { DeliveryLocation, GoodsData, ProductDetails, SelectedGoods, SelectedGoodsData } from '../../../models/Goods';
import GoodsService from '../../../services/GoodsService';
import NotificationService from '../../../services/NotificationService';
import {
  resetOrderGoods,
  selectOrderGoodsDeliveryDate,
  selectOrderGoodsDeliveryLocation,
  selectOrderGoodsList,
  selectOrderGoodsNotes,
  selectWorkTaskOrderInfo,
  setDeliveryDate,
  setNotes,
  setOrderGoodsList,
  setSelectedDeliveryLocation
} from '../../../stateManagement/reducers/orderGoodsReducer';
import { selectUserProfile } from '../../../stateManagement/reducers/userProfileReducer';
import { formatDateString } from '../../../utils/dateHandling';
import { log } from '../../../utils/logging/log';
import BasketPopover from '../components/BasketPopover';
import SelectedGoodsIcon from '../components/SelectedGoodsBadge';
import OrderSelectedGoodsList from '../components/OrderSelectedGoodsList';
import { Container, DialogContent, StyledTable } from '../components/Styles';
import { useGoodsList } from './goods-list/useGoodsList';
import ImageViewer from './ImageViewer';
import Alert from '../../../components/alert/Alert';
import { getInitialsFromEmail } from '../../../utils/initialsUtils';
import { getUserEmail } from '../../../utils/authProvider/authProvider';
import { AddIcon } from '../../../assets/icons/AddIcon';
import Groups from './Groups';
import CreateGroup from './CreateGroup';
import { CloseIcon } from '../../../assets/icons/CloseIcon';
import IconButton from '../../../components/icon-button/IconButton';
import { DialogBody } from '../../../stateManagement/reducers/confirmDialogReducer';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import n1LightTheme from '../../../theme/light-theme';
import ItemService from '../../../services/ItemService';
import ProductCatalogService from '../../../services/ProductCatalogService';
import { StarIcon } from '../../../assets/icons/StarIcon';
import OrdersService from '../../../services/OrdersService';

export enum OrderGoodsPage {
  SELECT = 'select',
  ORDER = 'order',
  PRODUCT_DETAILS = 'product-details'
}
interface Props {
  onClose: () => void;
  page: OrderGoodsPage;
  setPage: (page: OrderGoodsPage) => void;
  orderToUserVeicle: boolean;
  taskLocation?: LocationDTO;
  taskTypeId?: number;
  searchTerm?: string;
}

enum DefaultTabs {
  ALL = 'all',
  POPULAR = 'popular',
  GROUP_ID = 'groupId'
}

const OrderGoodsContent = (props: Props) => {
  const { page, setPage, onClose, orderToUserVeicle, taskLocation, taskTypeId, searchTerm } = props;

  const userProfile = useSelector(selectUserProfile).userProfile;
  const selectedGoods = useSelector(selectOrderGoodsList);
  const selectedDeliveryLocation = useSelector(selectOrderGoodsDeliveryLocation);
  const selectedWorkTaskOrderInfo = useSelector(selectWorkTaskOrderInfo);
  const deliveryDate = useSelector(selectOrderGoodsDeliveryDate);
  const notes = useSelector(selectOrderGoodsNotes);
  const [detailedGood, setDetailedGood] = useState<SelectedGoodsData>();

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allGoods, setAllGoods] = useState<ProductDetails[]>([]);
  const [goods, setGoods] = useState<ProductDetails[]>([]);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryLocation[] | undefined>(undefined);
  const [deliveryDateError, setDeliveryDateError] = useState(false);
  const [allowedDays, setAllowedDays] = useState<Date[] | null>([]);
  const [hasStockError, setHasStockError] = useState(true);
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [selectedGood, setSelectedGood] = useState<string>();
  const [groups, setGroups] = useState<FavoriteItemGroupDTO[]>([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DefaultTabs>(DefaultTabs.ALL);
  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const [popularProductsForTaskType, setPopularProductsForTaskType] = useState<ProductDetails[]>([]);
  const { getConfirmation } = useConfirmationDialog();

  useEffect(() => {
    if (!taskTypeId) return;
    OrdersService.getOrderedItems(taskTypeId)
      .then((response) => {
        const result: ProductDetails[] = [];

        response.forEach((item) => {
          const popularItem = allGoods.find((i) => i.itemId === item.itemId);
          if (popularItem) result.push(popularItem);
        });
        setPopularProductsForTaskType(result);
      })
      .catch((e) => {
        console.error(e);
      });
  }, [allGoods, taskTypeId]);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) {
      setGoods(allGoods);
      return;
    }

    const delayDebounce = setTimeout(() => {
      ProductCatalogService.searchProducts(searchTerm).then((res) => {
        setGoods(res);
      });
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const filteredGoods = useMemo(() => {
    switch (selectedGroup) {
      case DefaultTabs.ALL:
        return goods;
      case DefaultTabs.POPULAR:
        return popularProductsForTaskType;
      case DefaultTabs.GROUP_ID:
        const selectedFilter = groups.find((group) => group.groupId === selectedGroupId);
        return goods.filter((good) => good.itemId && selectedFilter?.itemIds?.includes(good.itemId ?? ''));
    }
  }, [goods, groups, popularProductsForTaskType, selectedGroup, selectedGroupId]);

  useEffect(() => {
    ItemService.getFavoriteItemGroups()
      .then((data) => {
        setGroups(data);
      })
      .catch(() => {
        NotificationService.error('Kunne ikke hente favoritlister');
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    GoodsService.getActiveProducts()
      .then((res) => {
        setAllGoods(res);
        setGoods(res);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke hente vareliste');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const fetchDeliveryDays = useCallback(
    async (warehouse?: string) => {
      if (!warehouse) return;
      try {
        const days = (await GoodsService.getAllowedDeliveryDates(warehouse)).map((str) => new Date(str));
        setAllowedDays(days);
        dispatch(setDeliveryDate(''));
      } catch (error) {
        log(error);
        NotificationService.error('Kan ikke hente tilladte datoer for det valgte leveringssted.');
      }
    },
    [dispatch]
  );

  useEffect(() => {
    GoodsService.getAllDeliveryDropPoints()
      .then((res) => {
        const options = res.map((dropPoint) => ({
          id: dropPoint?.warehouseId ?? '',
          name: dropPoint?.name ?? ''
        }));
        setDeliveryOptions(options);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kan ikke hente dropsteder.');
      });
  }, []);

  useEffect(() => {
    fetchDeliveryDays(selectedDeliveryLocation.id);
  }, [fetchDeliveryDays, selectedDeliveryLocation.id]);

  useEffect(() => {
    if (orderToUserVeicle) {
      dispatch(
        setSelectedDeliveryLocation({
          id: userProfile.vehicle?.vehicleId ?? '',
          name: userProfile.vehicle?.vehicleName ?? ''
        })
      );
    } else {
      dispatch(
        setSelectedDeliveryLocation({
          id: userProfile.favoriteDropPoint?.warehouseId ?? '',
          name: userProfile.favoriteDropPoint?.name ?? ''
        })
      );
    }
  }, [
    dispatch,
    orderToUserVeicle,
    userProfile.favoriteDropPoint?.name,
    userProfile.favoriteDropPoint?.warehouseId,
    userProfile.vehicle
  ]);

  useEffect(() => {
    if (selectedWorkTaskOrderInfo) {
      const fullNote = `${getInitialsFromEmail(getUserEmail())} ${selectedWorkTaskOrderInfo?.workTaskId} ${
        selectedWorkTaskOrderInfo?.address?.streetName
      } ${selectedWorkTaskOrderInfo?.address?.houseNumber}, ${selectedWorkTaskOrderInfo?.address?.postalCode} ${
        selectedWorkTaskOrderInfo?.address?.city
      }`;
      dispatch(setNotes(fullNote.substring(0, 40)));
    }
  }, [dispatch, selectedWorkTaskOrderInfo]);

  const selectedCount = useMemo(() => {
    let count = 0;
    selectedGoods.forEach((goods) => {
      count = count + (goods?.qty ?? 1);
    });
    return count;
  }, [selectedGoods]);

  const handleSelectGoods = useCallback(
    (goodsId?: string, goodsName?: string, amount?: number) => {
      if (!goodsId) return;
      const amountToAdd = amount ?? 1;
      let totalAddedOfGood = 1;

      const goodsToAdd: SelectedGoods = { name: goodsName, itemId: goodsId, qty: amountToAdd };

      if (selectedGoods.some((good) => good?.itemId === goodsToAdd?.itemId)) {
        let tmpSelectedGoods = selectedGoods.map((selectedGood) => {
          if (selectedGood.itemId === goodsToAdd?.itemId) {
            totalAddedOfGood = selectedGood?.qty ? selectedGood.qty + amountToAdd : amountToAdd;
            return { ...selectedGood, qty: totalAddedOfGood };
          } else {
            return selectedGood;
          }
        });
        dispatch(setOrderGoodsList(tmpSelectedGoods));
      } else {
        dispatch(setOrderGoodsList([...selectedGoods, { ...goodsToAdd }]));
        totalAddedOfGood = amountToAdd;
      }
      NotificationService.success(`${totalAddedOfGood} x ${goodsName} ligger nu i kurven`);
    },
    [dispatch, selectedGoods]
  );

  const submitOrder = useCallback(() => {
    if (selectedGoods.some((item) => (item.qty && item?.qty < 1) || !item.qty)) {
      NotificationService.error('Det er ikke muligt at bestille mindre end 1 af en vare');
      return;
    }
    if (!deliveryDate) {
      setDeliveryDateError(true);
      NotificationService.error('Ønsket leveringsdato mangler. Udfyld feltet for at fortsætte.');
      return;
    }
    if (!selectedDeliveryLocation.id) {
      NotificationService.error("Lager er ikke angivet. Tryk tilbage og udfyld feletet 'Til lager' for at fortsætte.");
      return;
    }

    setIsLoading(true);
    let body: CreateTransferOrderDTO = {
      warehouseId: selectedDeliveryLocation.id,
      description: notes,
      items: selectedGoods.map((good) => ({ itemId: good.itemId, qty: good.qty })),
      receiptDate: deliveryDate !== '' ? deliveryDate : undefined,
      workTaskId: selectedWorkTaskOrderInfo?.workTaskId ? parseInt(selectedWorkTaskOrderInfo.workTaskId) : undefined,
      projectId: selectedWorkTaskOrderInfo?.projectNumber,
      projectLegalEntityId: selectedWorkTaskOrderInfo?.projectLegalEntityId,
      taskTypeId
    };
    GoodsService.createTransferOrder(body)
      .then(() => {
        NotificationService.success('Ordre bestilt');
        dispatch(setOrderGoodsList([]));
        dispatch(resetOrderGoods());
        onClose();
      })
      .catch((error) => {
        NotificationService.error(`Kunne ikke oprette ordren: ${error}`);
        log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    selectedGoods,
    deliveryDate,
    selectedDeliveryLocation.id,
    notes,
    selectedWorkTaskOrderInfo?.workTaskId,
    selectedWorkTaskOrderInfo?.projectNumber,
    selectedWorkTaskOrderInfo?.projectLegalEntityId,
    dispatch,
    onClose,
    taskTypeId
  ]);

  const deliveryDateChanged = (date: Date) => {
    dispatch(setDeliveryDate(date.toISOString()));
    setDeliveryDateError(false);
  };

  const handleDeliveryLocation = useCallback(
    (deliveryLocation: DeliveryLocation) => {
      dispatch(setSelectedDeliveryLocation(deliveryLocation));
      fetchDeliveryDays(deliveryLocation.id);
    },
    [dispatch, fetchDeliveryDays]
  );

  const addToFavoriteList = useCallback((goodId: string) => {
    setSelectedGood(goodId);
    setShowGroups(true);
  }, []);

  const showDetailsHandler = useCallback((selectedGood: GoodsData) => {
    setDetailedGood(selectedGood);
    setProductDetailsOpen(true);
  }, []);

  const removeSelectedGoods = (goodsId?: string) => {
    if (!goodsId) return;
    const tmpSelectedGoods = selectedGoods.filter((selectedGood) => {
      return selectedGood.itemId !== goodsId;
    });
    dispatch(setOrderGoodsList(tmpSelectedGoods));
  };

  const createGroupCallback = (newGroup: FavoriteItemGroupDTO) => {
    setGroups((prev) => [...prev, newGroup]);
  };

  const updateSelectedFilter = (tab: DefaultTabs, groupId?: number) => {
    setSelectedGroup(tab);

    if (tab === DefaultTabs.GROUP_ID) {
      setSelectedGroupId(groupId);
    }
  };

  const deleteGroupHandler = async (groupId: number) => {
    const confirmation = await getConfirmation({
      headerText: `Er du sikker`,
      bodyText: `Er du sikker på at du vil slette denne gruppe? Valget kan ikke fortrydes`,
      declineButtonText: 'Fortryd',
      confirmButtonText: 'Slet'
    } as DialogBody);

    if (confirmation === 'confirm') {
      ItemService.deleteFavoriteItemGroup(groupId)
        .then(() => {
          setGroups((prev) => prev.filter((group) => group.groupId !== groupId));
        })
        .catch(() => {
          NotificationService.error('Kunne ikke slette gruppen');
        });
    }
  };

  const { tableInstance } = useGoodsList(filteredGoods, handleSelectGoods, addToFavoriteList, showDetailsHandler, groups);

  return (
    <>
      {isLoading && <LoadingOverlay />}
      {page === OrderGoodsPage.SELECT && (
        <>
          {selectedGood && showGroups && (
            <Groups
              handleClose={() => setShowGroups(false)}
              groups={groups}
              itemId={selectedGood}
              setGroupsCallback={(value) => setGroups(value)}
            />
          )}
          {showCreateGroup && (
            <CreateGroup handleClose={() => setShowCreateGroup(false)} createGroupCallback={createGroupCallback} />
          )}
          <TabsContainer>
            <StyledTabs>
              <Tab onClick={() => updateSelectedFilter(DefaultTabs.ALL)} active={selectedGroup === 'all'}>
                Alle varer
              </Tab>
              {popularProductsForTaskType.length > 0 && (
                <Tab onClick={() => updateSelectedFilter(DefaultTabs.POPULAR)} active={selectedGroup === 'popular'}>
                  <StarIcon margin="0px 5px 0px 0px" size="12px" />
                  Ofte bestilte
                </Tab>
              )}
              {groups.map((group) => (
                <Tab
                  large
                  onClick={() => updateSelectedFilter(DefaultTabs.GROUP_ID, group.groupId)}
                  active={selectedGroup === DefaultTabs.GROUP_ID && group.groupId === selectedGroupId}
                >
                  {group.name}
                  <StyledIconButton onClick={() => group.groupId && deleteGroupHandler(group.groupId)}>
                    <CloseIcon size="13px" color={n1LightTheme.palette.grey.black20} />
                  </StyledIconButton>
                </Tab>
              ))}
              <Tab onClick={() => setShowCreateGroup(true)}>
                <AddIcon size="16px" />
              </Tab>
            </StyledTabs>
            <TabsBackground />
          </TabsContainer>
          <Container>
            <DialogContent>
              <StyledTable tableInstance={tableInstance} loading={false} alwaysShowSort showPagination />
              <Footer>
                <BasicPopover
                  buttonElement={<SelectedGoodsIcon show count={selectedCount} />}
                  popoverElement={<BasketPopover removeGoods={removeSelectedGoods} selectedGoods={selectedGoods} />}
                  disablePopover={selectedCount === 0}
                  activeByClick
                />
                <Button disabled={selectedCount === 0} onClick={() => setPage(OrderGoodsPage.ORDER)}>
                  Gå til bestilling
                </Button>
              </Footer>
            </DialogContent>
          </Container>
        </>
      )}

      {page === OrderGoodsPage.ORDER && (
        <Container>
          <DialogContent>
            <OrderSelectedGoodsList
              selectedGoods={selectedGoods}
              showAvailable={false}
              showDeliverTo
              selectedDeliveryLocation={selectedDeliveryLocation}
              setDeliveryLocation={handleDeliveryLocation}
              deliveryOptions={deliveryOptions}
              clearSelectedGoods={() => dispatch(setOrderGoodsList([]))}
              renderFullHeight
              taskLocation={taskLocation}
              stockErrorCallback={(hasError) => setHasStockError(hasError)}
            />
            <Row>
              <StyledDatePicker
                required
                error={deliveryDateError}
                centerY
                disabled={selectedDeliveryLocation.id === ''}
                onDateChanged={(date) => deliveryDateChanged(date)}
                label="Ønsket leveringsdato"
                value={formatDateString(deliveryDate)}
                {...(allowedDays ? { allowedDays } : {})}
              />
              <TextField
                onChange={(e) => dispatch(setNotes(e.target.value))}
                label="Evt. bemærkning til ordren vil blive vist på pakkelabel"
                inputProps={{ maxLength: 40 }}
                helperText="Beskeden kan max indeholde 40 tegn"
                fullWidth
                value={notes}
                disabled={!!selectedWorkTaskOrderInfo}
              />
            </Row>
            <Footer>
              {hasStockError && (
                <Alert margin="0px 16px" severity="warning">
                  Én eller flere varer er ikke på lager. Dette kan betyde at din ordre ikke bliver leveret på den ønskede
                  dato.
                </Alert>
              )}
              <CriticalOrderPopover
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
              />
              <StyledButton
                disabled={selectedCount === 0 || (!selectedDeliveryLocation.id && !taskLocation)}
                onClick={submitOrder}
                isLoading={isLoading}
              >
                Bestil varer {selectedCount > 0 ? `(${selectedCount})` : ''}
              </StyledButton>
            </Footer>
          </DialogContent>
        </Container>
      )}
      {productDetailsOpen && (
        <ImageViewer
          handleClose={() => setProductDetailsOpen(false)}
          handleSelectGoods={handleSelectGoods}
          good={detailedGood}
          groups={groups}
          setGroupsCallback={(_groups) => setGroups(_groups)}
        />
      )}
    </>
  );
};

const StyledDatePicker = styled(DatePicker)`
  width: 50%;
  margin-right: ${(props) => props.theme.spacing(4)};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  margin: ${(props) => props.theme.spacing(5)} 0;
  justify-content: end;
  position: absolute;
  bottom: 135px;
  right: 30px;
  z-index: 2000;
`;

const Row = styled.div`
  margin-top: ${(props) => props.theme.spacing(5)};
  display: flex;
`;

const StyledButton = styled(Button)`
  margin-left: ${(props) => props.theme.spacing(4)};
`;

const StyledTabs = styled(Tabs)`
  padding-top: 15px;
  margin-left: ${(props) => props.theme.spacing(8)};
`;

const TabsContainer = styled.div`
  position: relative;
`;

const TabsBackground = styled.div`
  background-color: #dbdbdb;
  position: absolute;
  z-index: -2;
  top: 0;
  height: 100%;
  width: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: 25px;
  top: 8px;
  padding: 0;
`;

export default OrderGoodsContent;
